// import React, { useState, useMemo, useEffect } from 'react';
// import { Dialog, IconButton } from '@mui/material';
// import {
//   X,
//   Copy,
//   Check,
//   ChevronRight,
//   Layers,
//   Undo2,
//   AlertTriangle,
//   Info,
//   ArrowRight,
//   ArrowLeft,
// } from 'lucide-react';

// /**
//  * CopyToSelectionModal
//  *
//  * Props:
//  *  isOpen          – boolean
//  *  onClose         – () => void
//  *  selections      – Selection[]
//  *  currentSelId    – number
//  *  onCopy          – (sourceIds: number[], targetIds: number[], mode: 'append'|'replace') => void
//  *                     Copies rows FROM each id in sourceIds INTO each id in targetIds.
//  *  onUndoCopy      – (targetIds: number[], srcSelId: number) => void
//  *                     Removes rows in targetIds whose copyId came from srcSelId.
//  */
// export default function CopyToSelectionModal({
//   isOpen,
//   onClose,
//   selections,
//   currentSelId,
//   onCopy,
//   onUndoCopy,
// }) {
//   const [direction, setDirection] = useState('to'); // 'to' | 'from'
//   const [pickedIds, setPickedIds] = useState([]);   // targets (direction='to') or sources (direction='from')
//   const [mode, setMode] = useState('append');
//   const [undoKeys, setUndoKeys] = useState([]);      // `${direction}-${selId}`
//   const [tab, setTab] = useState('copy');            // 'copy' | 'undo'

//   const currentSel = selections.find(s => s.id === currentSelId);
//   const currentCount = currentSel?.rows.filter(r => r.productCode).length ?? 0;
//   const others = selections.filter(s => s.id !== currentSelId);

//   // Rows in OTHER selections that were copied FROM currentSelId (outgoing)
//   const copiedOutgoing = useMemo(() => {
//     return others
//       .map(sel => ({
//         sel,
//         copiedRows: sel.rows.filter(r => r.copyId && r.copyId.startsWith(`${currentSelId}-`)),
//         direction: 'outgoing',
//       }))
//       .filter(({ copiedRows }) => copiedRows.length > 0);
//   }, [others, currentSelId]);

//   // Rows in the CURRENT selection that were copied FROM other selections (incoming)
//   const copiedIncoming = useMemo(() => {
//     const currentRows = currentSel?.rows ?? [];
//     return others
//       .map(sel => ({
//         sel,
//         copiedRows: currentRows.filter(r => r.copyId && r.copyId.startsWith(`${sel.id}-`)),
//         direction: 'incoming',
//       }))
//       .filter(({ copiedRows }) => copiedRows.length > 0);
//   }, [others, currentSel]);

//   const undoEntries = useMemo(() => [...copiedOutgoing, ...copiedIncoming], [copiedOutgoing, copiedIncoming]);
//   const hasCopiedData = undoEntries.length > 0;


//   useEffect(() => {
//     if (!hasCopiedData && tab === 'undo') {
//       setTab('copy');
//     }
//   }, [hasCopiedData, tab]);


//   const togglePicked = (id) =>
//     setPickedIds(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));

//   const toggleUndoTarget = (key) =>
//     setUndoKeys(p => (p.includes(key) ? p.filter(x => x !== key) : [...p, key]));

//   const switchDirection = (dir) => {
//     setDirection(dir);
//     setPickedIds([]);
//   };

//   const handleCopy = () => {
//     if (pickedIds.length === 0) return;
//     if (direction === 'to') {
//       onCopy([currentSelId], pickedIds, mode);
//     } else {
//       onCopy(pickedIds, [currentSelId], mode);
//     }
//     setPickedIds([]);
//     onClose();
//   };

//   const handleUndo = () => {
//     if (undoKeys.length === 0) return;
//     undoKeys.forEach(key => {
//       const [dir, idStr] = key.split('-');
//       const selId = Number(idStr);
//       if (dir === 'outgoing') {
//         onUndoCopy?.([selId], currentSelId);
//       } else {
//         onUndoCopy?.([currentSelId], selId);
//       }
//     });
//     setUndoKeys([]);
//     if (undoKeys.length === undoEntries.length) onClose();
//   };

//   const handleClose = () => {
//     setPickedIds([]);
//     setUndoKeys([]);
//     setDirection('to');
//     setTab('copy');   
//     onClose();
//   };

//   return (
//     <Dialog
//       open={isOpen}
//       onClose={handleClose}
//       maxWidth="xs"
//       fullWidth
//       slotProps={{
//         backdrop: {
//           style: { backgroundColor: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(4px)' },
//         },
//       }}
//       PaperProps={{
//         sx: {
//           borderRadius: '16px',
//           fontFamily: "'DM Sans', system-ui, sans-serif",
//           overflow: 'hidden',
//           display: 'flex',
//           flexDirection: 'column',
//           maxHeight: '88vh',
//         },
//       }}
//     >
//       {/* ── Header ─────────────────────────────────────────────────────────── */}
//       <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-slate-100">
//         <div className="flex items-center gap-3">
//           <div className="w-8 h-8 rounded-lg bg-violet-600 flex items-center justify-center">
//             <Copy size={15} className="text-white" />
//           </div>
//           <div>
//             <h2 className="text-sm font-bold text-slate-900">Copy Selections</h2>
//             {tab === 'copy' && direction === 'to' && (
//               <p className="text-[11px] text-slate-400 mt-0.5">
//                 Copying{' '}
//                 <span className="font-semibold text-slate-600">
//                   {currentCount} product{currentCount !== 1 ? 's' : ''}
//                 </span>{' '}
//                 from{' '}
//                 <span className="font-semibold text-violet-600">{currentSel?.name}</span>
//               </p>
//             )}
//             {tab === 'copy' && direction === 'from' && (
//               <p className="text-[11px] text-slate-400 mt-0.5">
//                 Copying into{' '}
//                 <span className="font-semibold text-violet-600">{currentSel?.name}</span>
//               </p>
//             )}
//             {tab === 'undo' && (
//               <p className="text-[11px] text-slate-400 mt-0.5">
//                 Manage copied rows for{' '}
//                 <span className="font-semibold text-violet-600">{currentSel?.name}</span>
//               </p>
//             )}
//           </div>
//         </div>
//         <IconButton onClick={handleClose} size="small">
//           <X size={16} />
//         </IconButton>
//       </div>

//       {/* ── Tab bar (only shown when there is something to undo) ──────────── */}
//       {hasCopiedData && (
//         <div className="flex border-b border-slate-100 bg-slate-50">
//           {[
//             { key: 'copy', label: 'Copy', icon: <Copy size={11} /> },
//             { key: 'undo', label: 'Undo Copy', icon: <Undo2 size={11} />, badge: undoEntries.length },
//           ].map(t => (
//             <button
//               key={t.key}
//               onClick={() => setTab(t.key)}
//               className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[11px] font-bold transition-all border-b-2 ${tab === t.key
//                   ? t.key === 'undo'
//                     ? 'border-amber-500 text-amber-600 bg-amber-50/60'
//                     : 'border-violet-600 text-violet-700 bg-white'
//                   : 'border-transparent text-slate-400 hover:text-slate-600'
//                 }`}
//             >
//               {t.icon}
//               {t.label}
//               {t.badge != null && (
//                 <span
//                   className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${tab === 'undo' ? 'bg-amber-500 text-white' : 'bg-slate-200 text-slate-500'
//                     }`}
//                 >
//                   {t.badge}
//                 </span>
//               )}
//             </button>
//           ))}
//         </div>
//       )}

//       {/* ══ COPY TAB ══════════════════════════════════════════════════════════ */}
//       {tab === 'copy' && (
//         <>
//           {/* Direction toggle */}
//           <div className="px-5 pt-3 pb-2 border-b border-slate-100 bg-slate-50">
//             <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Direction</p>
//             <div className="flex gap-2">
//               <button
//                 onClick={() => switchDirection('to')}
//                 className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border text-[11px] font-semibold transition-all ${direction === 'to'
//                     ? 'bg-violet-50 border-violet-300 text-violet-700'
//                     : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
//                   }`}
//               >
//                 <ArrowRight size={12} />
//                 To other selections
//               </button>
//               <button
//                 onClick={() => switchDirection('from')}
//                 className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border text-[11px] font-semibold transition-all ${direction === 'from'
//                     ? 'bg-violet-50 border-violet-300 text-violet-700'
//                     : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
//                   }`}
//               >
//                 <ArrowLeft size={12} />
//                 From other selections
//               </button>
//             </div>
//           </div>

//           {/* Copy mode */}
//           <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
//             <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Copy Mode</p>
//             <div className="flex gap-2">
//               {[
//                 {
//                   val: 'append',
//                   label: 'Append',
//                   desc: direction === 'to' ? 'Add to existing products' : 'Add to current selection',
//                 },
//                 {
//                   val: 'replace',
//                   label: 'Replace',
//                   desc: direction === 'to' ? 'Clear and replace all' : 'Clear current, then replace',
//                 },
//               ].map(opt => (
//                 <button
//                   key={opt.val}
//                   onClick={() => setMode(opt.val)}
//                   className={`flex-1 flex items-start gap-2 px-3 py-2 rounded-xl border text-left transition-all ${mode === opt.val ? 'bg-violet-50 border-violet-300' : 'bg-white border-slate-200 hover:border-slate-300'
//                     }`}
//                 >
//                   <div
//                     className={`w-4 h-4 rounded-full border-2 mt-0.5 flex-shrink-0 flex items-center justify-center transition-all ${mode === opt.val ? 'border-violet-600 bg-violet-600' : 'border-slate-300'
//                       }`}
//                   >
//                     {mode === opt.val && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
//                   </div>
//                   <div>
//                     <p className={`text-xs font-bold ${mode === opt.val ? 'text-violet-700' : 'text-slate-700'}`}>
//                       {opt.label}
//                     </p>
//                     <p className="text-[10px] text-slate-400">{opt.desc}</p>
//                   </div>
//                 </button>
//               ))}
//             </div>
//           </div>

//           {/* Target / Source selection list */}
//           <div className="flex-1 overflow-y-auto px-5 py-3">
//             <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
//               {direction === 'to' ? 'Select Target Selections' : 'Select Source Selections'}
//             </p>
//             <div className="space-y-1.5">
//               {others.map(sel => {
//                 const cnt = sel.rows.filter(r => r.productCode).length;
//                 const checked = pickedIds.includes(sel.id);

//                 const alreadyCopiedCount =
//                   direction === 'to'
//                     ? sel.rows.filter(r => r.copyId && r.copyId.startsWith(`${currentSelId}-`)).length
//                     : (currentSel?.rows ?? []).filter(r => r.copyId && r.copyId.startsWith(`${sel.id}-`)).length;

//                 return (
//                   <button
//                     key={sel.id}
//                     onClick={() => togglePicked(sel.id)}
//                     className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all text-left ${checked
//                         ? 'bg-violet-50 border-violet-300'
//                         : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50'
//                       }`}
//                   >
//                     <div
//                       className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${checked ? 'bg-violet-600 border-violet-600' : 'border-slate-300'
//                         }`}
//                     >
//                       {checked && <Check size={11} className="text-white" strokeWidth={3} />}
//                     </div>

//                     <div className="flex-1 min-w-0">
//                       <p className={`text-xs font-semibold ${checked ? 'text-violet-700' : 'text-slate-700'}`}>
//                         {sel.name}
//                       </p>
//                       <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
//                         <p className="text-[10px] text-slate-400">
//                           {cnt > 0 ? `${cnt} product${cnt !== 1 ? 's' : ''}` : 'Empty'}
//                         </p>
//                         {alreadyCopiedCount > 0 && (
//                           <span className="text-[9px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full flex items-center gap-1">
//                             <Copy size={8} />
//                             {direction === 'to'
//                               ? `${alreadyCopiedCount} copied here`
//                               : `${alreadyCopiedCount} already copied in`}
//                           </span>
//                         )}
//                       </div>
//                     </div>

//                     <ChevronRight size={14} className={`flex-shrink-0 ${checked ? 'text-violet-400' : 'text-slate-300'}`} />
//                   </button>
//                 );
//               })}
//             </div>
//           </div>

//           {/* Footer */}
//           <div className="px-5 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
//             <p className="text-[11px] text-slate-500">
//               {pickedIds.length > 0 ? (
//                 <>
//                   <span className="font-semibold text-slate-700">{pickedIds.length}</span>{' '}
//                   selection{pickedIds.length !== 1 ? 's' : ''} selected
//                 </>
//               ) : direction === 'to' ? (
//                 'Select at least one target'
//               ) : (
//                 'Select at least one source'
//               )}
//             </p>
//             <div className="flex gap-2">
//               <button
//                 onClick={handleClose}
//                 className="px-4 py-2 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={handleCopy}
//                 disabled={pickedIds.length === 0}
//                 className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl transition-all shadow-sm"
//               >
//                 <Layers size={13} />
//                 {direction === 'to' ? (
//                   <>Copy to {pickedIds.length || ''} Selection{pickedIds.length !== 1 ? 's' : ''}</>
//                 ) : (
//                   <>Copy from {pickedIds.length || ''} Selection{pickedIds.length !== 1 ? 's' : ''}</>
//                 )}
//               </button>
//             </div>
//           </div>
//         </>
//       )}

//       {/* ══ UNDO TAB ══════════════════════════════════════════════════════════ */}
//       {tab === 'undo' && (
//         <>
//           <div className="mx-5 mt-3 mb-1 flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl">
//             <Info size={13} className="text-amber-500 mt-0.5 flex-shrink-0" />
//             <p className="text-[11px] text-amber-700 leading-relaxed">
//               These are copy relationships involving <strong>{currentSel?.name}</strong>. Undoing will{' '}
//               <strong>remove only the copied rows</strong> (rows added manually are kept).
//             </p>
//           </div>

//           <div className="flex-1 overflow-y-auto px-5 py-3">
//             <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Select Copies to Undo</p>
//             <div className="space-y-1.5">
//               {undoEntries.map(({ sel, copiedRows, direction: dir }) => {
//                 const key = `${dir}-${sel.id}`;
//                 const checked = undoKeys.includes(key);
//                 const totalRows = dir === 'outgoing' ? sel.rows.filter(r => r.productCode).length : currentCount;

//                 return (
//                   <button
//                     key={key}
//                     onClick={() => toggleUndoTarget(key)}
//                     className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all text-left ${checked ? 'bg-red-50 border-red-300' : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50'
//                       }`}
//                   >
//                     <div
//                       className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${checked ? 'bg-red-500 border-red-500' : 'border-slate-300'
//                         }`}
//                     >
//                       {checked && <Check size={11} className="text-white" strokeWidth={3} />}
//                     </div>

//                     <div className="flex-1 min-w-0">
//                       <div className="flex items-center gap-1.5">
//                         {dir === 'outgoing' ? (
//                           <ArrowRight size={10} className="text-slate-400 flex-shrink-0" />
//                         ) : (
//                           <ArrowLeft size={10} className="text-slate-400 flex-shrink-0" />
//                         )}
//                         <p className={`text-xs font-semibold ${checked ? 'text-red-700' : 'text-slate-700'}`}>
//                           {dir === 'outgoing'
//                             ? `${sel.name} (received from ${currentSel?.name})`
//                             : `${sel.name} (copied into ${currentSel?.name})`}
//                         </p>
//                       </div>
//                       <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
//                         <span className="text-[10px] text-slate-400">{totalRows} total</span>
//                         <span className="text-[9px] font-semibold text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full">
//                           {copiedRows.length} will be removed
//                         </span>
//                         <span className="text-[9px] font-medium text-emerald-600 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full">
//                           {totalRows - copiedRows.length} kept
//                         </span>
//                       </div>
//                     </div>

//                     {checked && <AlertTriangle size={13} className="text-red-400 flex-shrink-0" />}
//                   </button>
//                 );
//               })}
//             </div>
//           </div>

//           <div className="px-5 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
//             <p className="text-[11px] text-slate-500">
//               {undoKeys.length > 0 ? (
//                 <>
//                   Removing copied rows from{' '}
//                   <span className="font-semibold text-red-600">{undoKeys.length}</span>{' '}
//                   relationship{undoKeys.length !== 1 ? 's' : ''}
//                 </>
//               ) : (
//                 'Select relationships to undo'
//               )}
//             </p>
//             <div className="flex gap-2">
//               <button
//                 onClick={handleClose}
//                 className="px-4 py-2 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={handleUndo}
//                 disabled={undoKeys.length === 0}
//                 className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-red-500 hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl transition-all shadow-sm"
//               >
//                 <Undo2 size={13} /> Undo {undoKeys.length || ''}
//               </button>
//             </div>
//           </div>
//         </>
//       )}
//     </Dialog>
//   );
// }

import React, { useState, useMemo, useEffect } from 'react';
import { Dialog, IconButton } from '@mui/material';
import {
  X,
  Copy,
  Check,
  Layers,
  Undo2,
  AlertTriangle,
  Info,
  ArrowRight,
  ArrowLeft,
  Search,
  PackageOpen,
} from 'lucide-react';

/**
 * CopyToSelectionModal
 *
 * Layout:
 *   Header
 *   Tabs (only when there is something to undo)
 *   Control bar  – Direction + Copy mode (always on top)
 *   Two panes    – Left: target / source selections
 *                  Right: SKUs with per-SKU select / deselect
 *   Footer
 *
 * Props:
 *  isOpen          – boolean
 *  onClose         – () => void
 *  selections      – Selection[]
 *  currentSelId    – number
 *  onCopy          – (sourceIds: number[], targetIds: number[], mode: 'append'|'replace',
 *                     selectedProducts?: { [sourceSelId]: productCode[] }) => void
 *                     selectedProducts is undefined when nothing was deselected (copy everything).
 *                     When defined, ONLY the listed product codes must be copied per source.
 *  onUndoCopy      – (targetIds: number[], srcSelId: number) => void
 */

const sectionLabel = 'text-xs font-semibold text-slate-500';

const Segmented = ({ value, onChange, options }) => (
  <div className="inline-flex p-0.5 bg-slate-200/70 rounded-lg">
    {options.map(o => (
      <button
        key={o.value}
        type="button"
        onClick={() => onChange(o.value)}
        className={`flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold rounded-md transition-all ${
          value === o.value
            ? 'bg-white text-blue-700 shadow-sm'
            : 'text-slate-600 hover:text-slate-800'
        }`}
      >
        {o.icon}
        {o.label}
      </button>
    ))}
  </div>
);

export default function CopyToSelectionModal({
  isOpen,
  onClose,
  selections,
  currentSelId,
  onCopy,
  onUndoCopy,
}) {
  const [direction, setDirection] = useState('to'); // 'to' | 'from'
  const [pickedIds, setPickedIds] = useState([]);   // targets (direction='to') or sources (direction='from')
  const [mode, setMode] = useState('append');
  const [undoKeys, setUndoKeys] = useState([]);      // `${direction}-${selId}`
  const [tab, setTab] = useState('copy');            // 'copy' | 'undo'
  const [excludedProducts, setExcludedProducts] = useState([]); // `${selId}::${productCode}`
  const [search, setSearch] = useState('');

  const currentSel = selections.find(s => s.id === currentSelId);
  const others = selections.filter(s => s.id !== currentSelId);

  // Rows in OTHER selections that were copied FROM currentSelId (outgoing)
  const copiedOutgoing = useMemo(() => {
    return others
      .map(sel => ({
        sel,
        copiedRows: sel.rows.filter(r => r.copyId && r.copyId.startsWith(`${currentSelId}-`)),
        direction: 'outgoing',
      }))
      .filter(({ copiedRows }) => copiedRows.length > 0);
  }, [others, currentSelId]);

  // Rows in the CURRENT selection that were copied FROM other selections (incoming)
  const copiedIncoming = useMemo(() => {
    const currentRows = currentSel?.rows ?? [];
    return others
      .map(sel => ({
        sel,
        copiedRows: currentRows.filter(r => r.copyId && r.copyId.startsWith(`${sel.id}-`)),
        direction: 'incoming',
      }))
      .filter(({ copiedRows }) => copiedRows.length > 0);
  }, [others, currentSel]);

  const undoEntries = useMemo(() => [...copiedOutgoing, ...copiedIncoming], [copiedOutgoing, copiedIncoming]);
  const hasCopiedData = undoEntries.length > 0;
  const currentCount = currentSel?.rows.filter(r => r.productCode).length ?? 0;

  // ── Product-level selection ───────────────────────────────────────────────
  const productKey = (selId, code) => `${selId}::${code}`;

  // Which selections are we copying products FROM right now?
  const sourceGroups = useMemo(() => {
    const srcSels =
      direction === 'to'
        ? (currentSel ? [currentSel] : [])
        : others.filter(s => pickedIds.includes(s.id));

    return srcSels.map(sel => {
      const seen = new Map(); // de-dupe by productCode
      sel.rows.forEach(r => {
        if (r.productCode && !seen.has(r.productCode)) seen.set(r.productCode, r);
      });
      return { sel, products: [...seen.values()] };
    });
  }, [direction, currentSel, others, pickedIds]);

  // Same groups, filtered by the search box (display only)
  const visibleGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sourceGroups;
    return sourceGroups
      .map(g => ({
        ...g,
        products: g.products.filter(p =>
          `${p.productCode} ${p.productName ?? ''} ${p.description ?? ''}`.toLowerCase().includes(q)
        ),
      }))
      .filter(g => g.products.length > 0);
  }, [sourceGroups, search]);

  const isOn = (selId, code) => !excludedProducts.includes(productKey(selId, code));

  const totalProducts = sourceGroups.reduce((n, g) => n + g.products.length, 0);
  const selectedProductCount = sourceGroups.reduce(
    (n, g) => n + g.products.filter(p => isOn(g.sel.id, p.productCode)).length,
    0
  );

  const toggleProduct = (selId, code) => {
    const key = productKey(selId, code);
    setExcludedProducts(p => (p.includes(key) ? p.filter(x => x !== key) : [...p, key]));
  };

  const setProductsOn = (group, products, on) => {
    const keys = products.map(p => productKey(group.sel.id, p.productCode));
    setExcludedProducts(p => (on ? p.filter(k => !keys.includes(k)) : [...new Set([...p, ...keys])]));
  };

  const setAllVisible = (on) => {
    const keys = visibleGroups.flatMap(g => g.products.map(p => productKey(g.sel.id, p.productCode)));
    setExcludedProducts(p => (on ? p.filter(k => !keys.includes(k)) : [...new Set([...p, ...keys])]));
  };
  // ──────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!hasCopiedData && tab === 'undo') setTab('copy');
  }, [hasCopiedData, tab]);

  const togglePicked = (id) =>
    setPickedIds(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));

  const allOthersPicked = others.length > 0 && pickedIds.length === others.length;
  const togglePickAll = () => setPickedIds(allOthersPicked ? [] : others.map(s => s.id));

  const toggleUndoTarget = (key) =>
    setUndoKeys(p => (p.includes(key) ? p.filter(x => x !== key) : [...p, key]));

  const switchDirection = (dir) => {
    if (dir === direction) return;
    setDirection(dir);
    setPickedIds([]);
    setExcludedProducts([]);
    setSearch('');
  };

  const resetState = () => {
    setPickedIds([]);
    setUndoKeys([]);
    setExcludedProducts([]);
    setSearch('');
    setDirection('to');
    setMode('append');
    setTab('copy');
  };

  const handleCopy = () => {
    if (pickedIds.length === 0 || selectedProductCount === 0) return;

    // undefined = "copy everything"; otherwise an explicit whitelist per source selection
    let selectedProducts;
    const anyExcluded = sourceGroups.some(g =>
      g.products.some(p => !isOn(g.sel.id, p.productCode))
    );
    if (anyExcluded) {
      selectedProducts = {};
      sourceGroups.forEach(g => {
        selectedProducts[g.sel.id] = g.products
          .filter(p => isOn(g.sel.id, p.productCode))
          .map(p => p.productCode);
      });
    }

    if (direction === 'to') {
      onCopy([currentSelId], pickedIds, mode, selectedProducts);
    } else {
      onCopy(pickedIds, [currentSelId], mode, selectedProducts);
    }
    resetState();
    onClose();
  };

  const handleUndo = () => {
    if (undoKeys.length === 0) return;
    undoKeys.forEach(key => {
      const [dir, idStr] = key.split('-');
      const selId = Number(idStr);
      if (dir === 'outgoing') {
        onUndoCopy?.([selId], currentSelId);
      } else {
        onUndoCopy?.([currentSelId], selId);
      }
    });
    setUndoKeys([]);
    if (undoKeys.length === undoEntries.length) onClose();
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const modeHint =
    mode === 'append'
      ? direction === 'to'
        ? 'Adds to the products already in each target. Duplicates are skipped.'
        : 'Adds to the products already in this selection. Duplicates are skipped.'
      : direction === 'to'
        ? 'Clears each target first, then copies.'
        : 'Clears this selection first, then copies.';

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth={false}
      slotProps={{
        backdrop: {
          style: { backgroundColor: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(4px)' },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: '14px',
          fontFamily: "'DM Sans', system-ui, sans-serif",
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          width: 'min(1080px, calc(100% - 32px))',
          height: 'min(780px, 92vh)',
          maxHeight: '92vh',
          m: 2,
        },
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center">
            <Copy size={16} className="text-white" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-slate-900">Copy Selections</h2>
            {tab === 'copy' && direction === 'to' && (
              <p className="text-xs text-slate-500 mt-0.5">
                Copying{' '}
                <span className="font-semibold text-slate-700">
                  {selectedProductCount} of {currentCount} product{currentCount !== 1 ? 's' : ''}
                </span>{' '}
                from <span className="font-semibold text-blue-700">{currentSel?.name}</span>
              </p>
            )}
            {tab === 'copy' && direction === 'from' && (
              <p className="text-xs text-slate-500 mt-0.5">
                Copying into <span className="font-semibold text-blue-700">{currentSel?.name}</span>
              </p>
            )}
            {tab === 'undo' && (
              <p className="text-xs text-slate-500 mt-0.5">
                Manage copied rows for <span className="font-semibold text-blue-700">{currentSel?.name}</span>
              </p>
            )}
          </div>
        </div>
        <IconButton onClick={handleClose} size="small">
          <X size={18} />
        </IconButton>
      </div>

      {/* ── Tab bar (only shown when there is something to undo) ──────────── */}
      {hasCopiedData && (
        <div className="flex border-b border-slate-200 bg-slate-50 px-3">
          {[
            { key: 'copy', label: 'Copy', icon: <Copy size={13} /> },
            { key: 'undo', label: 'Undo Copy', icon: <Undo2 size={13} />, badge: undoEntries.length },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold transition-all border-b-2 ${
                tab === t.key
                  ? t.key === 'undo'
                    ? 'border-amber-500 text-amber-700'
                    : 'border-blue-600 text-blue-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {t.icon}
              {t.label}
              {t.badge != null && (
                <span
                  className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                    tab === 'undo' ? 'bg-amber-500 text-white' : 'bg-slate-200 text-slate-600'
                  }`}
                >
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* ══ COPY TAB ══════════════════════════════════════════════════════════ */}
      {tab === 'copy' && (
        <>
          {/* ── Control bar: direction + mode ─────────────────────────────── */}
          <div className="px-6 py-3.5 border-b border-slate-200 bg-slate-50/70 flex flex-wrap items-start gap-x-10 gap-y-3">
            <div>
              {/* <p className={`${sectionLabel} mb-1.5`}>Direction</p> */}
              <Segmented
                value={direction}
                onChange={switchDirection}
                options={[
                  { value: 'to', label: 'To other selections', icon: <ArrowRight size={13} /> },
                  { value: 'from', label: 'From other selections', icon: <ArrowLeft size={13} /> },
                ]}
              />
            </div>

            <div>
              {/* <p className={`${sectionLabel} mb-1.5`}>Copy mode</p> */}
              <div className="flex flex-wrap items-center gap-3">
                <Segmented
                  value={mode}
                  onChange={setMode}
                  options={[
                    { value: 'append', label: 'Append' },
                    { value: 'replace', label: 'Replace' },
                  ]}
                />
                <p
                  className={`text-[11px] max-w-[320px] leading-snug flex items-start gap-1.5 ${
                    mode === 'replace' ? 'text-amber-700' : 'text-slate-500'
                  }`}
                >
                  {mode === 'replace' && <AlertTriangle size={12} className="mt-px flex-shrink-0" />}
                  {modeHint}
                </p>
              </div>
            </div>
          </div>

          {/* ── Two panes ─────────────────────────────────────────────────── */}
          <div className="flex-1 min-h-0 flex flex-col md:flex-row overflow-y-auto md:overflow-hidden">
            {/* LEFT: target / source selections */}
            <div className="md:w-[300px] md:flex-shrink-0 flex flex-col md:min-h-0 border-b md:border-b-0 md:border-r border-slate-200 bg-white">
              <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-slate-100">
                <div>
                  <p className="text-sm font-bold text-slate-900">
                    {direction === 'to' ? 'Target selections' : 'Source selections'}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    <span className="font-semibold text-blue-700">{pickedIds.length}</span> of {others.length} selected
                  </p>
                </div>
                <button
                  onClick={togglePickAll}
                  disabled={others.length === 0}
                  className="px-2.5 py-1.5 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed rounded-md transition-colors"
                >
                  {allOthersPicked ? 'Clear' : 'Select all'}
                </button>
              </div>

              <div className="flex-1 md:min-h-0 md:overflow-y-auto px-4 py-3 space-y-1.5 max-h-[260px] md:max-h-none overflow-y-auto">
                {others.length === 0 && (
                  <p className="text-xs text-slate-500 text-center py-6">No other selections available.</p>
                )}
                {others.map(sel => {
                  const cnt = sel.rows.filter(r => r.productCode).length;
                  const checked = pickedIds.includes(sel.id);

                  const alreadyCopiedCount =
                    direction === 'to'
                      ? sel.rows.filter(r => r.copyId && r.copyId.startsWith(`${currentSelId}-`)).length
                      : (currentSel?.rows ?? []).filter(r => r.copyId && r.copyId.startsWith(`${sel.id}-`)).length;

                  return (
                    <button
                      key={sel.id}
                      onClick={() => togglePicked(sel.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all text-left ${
                        checked
                          ? 'bg-blue-50 border-blue-500 shadow-sm'
                          : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div
                        className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                          checked ? 'bg-blue-600 border-blue-600' : 'border-slate-300'
                        }`}
                      >
                        {checked && <Check size={10} className="text-white" strokeWidth={3} />}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className={`text-xs font-semibold truncate ${checked ? 'text-blue-700' : 'text-slate-800'}`}>
                          {sel.name}
                        </p>
                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                          <p className="text-[11px] text-slate-500">
                            {cnt > 0 ? `${cnt} product${cnt !== 1 ? 's' : ''}` : 'Empty'}
                          </p>
                          {alreadyCopiedCount > 0 && (
                            <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full flex items-center gap-1">
                              <Copy size={8} />
                              {direction === 'to'
                                ? `${alreadyCopiedCount} copied here`
                                : `${alreadyCopiedCount} already copied in`}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* RIGHT: SKUs */}
            <div className="flex-1 min-w-0 flex flex-col bg-white min-h-[320px] md:min-h-0">
              <div className="px-5 pt-4 pb-3 border-b border-slate-200">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <p className="text-sm font-bold text-slate-900">Products to copy</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      <span className="font-semibold text-blue-700">{selectedProductCount}</span> of {totalProducts} selected
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => setAllVisible(true)}
                      disabled={totalProducts === 0}
                      className="px-2.5 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed rounded-md transition-colors"
                    >
                      Select all
                    </button>
                    <button
                      onClick={() => setAllVisible(false)}
                      disabled={totalProducts === 0}
                      className="px-2.5 py-1.5 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed rounded-md transition-colors"
                    >
                      Clear
                    </button>
                  </div>
                </div>

                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search by SKU or name"
                    disabled={totalProducts === 0}
                    className="w-full pl-9 pr-3 py-2 text-xs text-slate-800 placeholder:text-slate-400 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:opacity-50"
                  />
                </div>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto">
                {totalProducts === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center px-8 py-10">
                    <div className="w-11 h-11 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                      <PackageOpen size={20} className="text-slate-400" />
                    </div>
                    <p className="text-sm font-semibold text-slate-700">
                      {direction === 'from' && pickedIds.length === 0 ? 'No source selected' : 'No products found'}
                    </p>
                    <p className="text-xs text-slate-500 mt-1 max-w-[260px]">
                      {direction === 'from' && pickedIds.length === 0
                        ? 'Pick one or more source selections on the left to see their products here.'
                        : 'The selected selections have no products to copy.'}
                    </p>
                  </div>
                ) : visibleGroups.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center px-8 py-10">
                    <p className="text-sm font-semibold text-slate-700">No matches</p>
                    <p className="text-xs text-slate-500 mt-1">Nothing matches “{search}”.</p>
                  </div>
                ) : (
                  visibleGroups.map(group => {
                    const fullGroup = sourceGroups.find(g => g.sel.id === group.sel.id);
                    const groupSelected = fullGroup.products.filter(p => isOn(group.sel.id, p.productCode)).length;
                    const visibleAllOn = group.products.every(p => isOn(group.sel.id, p.productCode));

                    return (
                      <div key={group.sel.id}>
                        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-2 bg-slate-100 border-b border-slate-200">
                          <button
                            onClick={() => setProductsOn(group, group.products, !visibleAllOn)}
                            className="flex items-center gap-2.5 min-w-0 text-left"
                          >
                            <div
                              className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                visibleAllOn ? 'bg-blue-600 border-blue-600' : 'border-slate-400 bg-white'
                              }`}
                            >
                              {visibleAllOn && <Check size={10} className="text-white" strokeWidth={3} />}
                            </div>
                            <span className="text-xs font-bold text-slate-800 truncate">{group.sel.name}</span>
                          </button>
                          <span className="text-[11px] font-medium text-slate-500 flex-shrink-0">
                            {groupSelected}/{fullGroup.products.length} selected
                          </span>
                        </div>

                        <div className="divide-y divide-slate-100">
                          {group.products.map(p => {
                            const on = isOn(group.sel.id, p.productCode);
                            return (
                              <button
                                key={p.productCode}
                                onClick={() => toggleProduct(group.sel.id, p.productCode)}
                                className={`w-full flex items-center gap-3 px-5 py-2.5 text-left transition-colors hover:bg-blue-50/50 ${
                                  on ? 'bg-white' : 'bg-slate-50'
                                }`}
                              >
                                <div
                                  className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                                    on ? 'bg-blue-600 border-blue-600' : 'border-slate-300 bg-white'
                                  }`}
                                >
                                  {on && <Check size={10} className="text-white" strokeWidth={3} />}
                                </div>
                                <div className={`min-w-0 flex-1 ${on ? '' : 'opacity-50'}`}>
                                  <p className="text-xs font-semibold text-slate-800 truncate">{p.productCode}</p>
                                  {(p.productName || p.description) && (
                                    <p className="text-[11px] text-slate-500 truncate mt-0.5">
                                      {p.productName || p.description}
                                    </p>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-3.5 border-t border-slate-200 bg-white flex items-center justify-between gap-3">
            <p className="text-xs text-slate-500">
              {pickedIds.length > 0 ? (
                <>
                  <span className="font-semibold text-slate-800">{pickedIds.length}</span>{' '}
                  selection{pickedIds.length !== 1 ? 's' : ''}
                  <span className="text-slate-400"> · </span>
                  <span className="font-semibold text-slate-800">{selectedProductCount}</span>{' '}
                  product{selectedProductCount !== 1 ? 's' : ''} will be copied
                </>
              ) : direction === 'to' ? (
                'Select at least one target'
              ) : (
                'Select at least one source'
              )}
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCopy}
                disabled={pickedIds.length === 0 || selectedProductCount === 0}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg transition-all shadow-sm"
              >
                <Layers size={13} />
                {direction === 'to' ? (
                  <>Copy to {pickedIds.length || ''} Selection{pickedIds.length !== 1 ? 's' : ''}</>
                ) : (
                  <>Copy from {pickedIds.length || ''} Selection{pickedIds.length !== 1 ? 's' : ''}</>
                )}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ══ UNDO TAB ══════════════════════════════════════════════════════════ */}
      {tab === 'undo' && (
        <>
          <div className="flex-1 min-h-0 overflow-y-auto bg-slate-50/70">
            <div className="max-w-2xl mx-auto px-6 py-5">
              <div className="flex items-start gap-2 px-3.5 py-3 bg-amber-50 border border-amber-200 rounded-lg mb-5">
                <Info size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-800 leading-relaxed">
                  These are copy relationships involving <strong>{currentSel?.name}</strong>. Undoing will{' '}
                  <strong>remove only the copied rows</strong> (rows added manually are kept).
                </p>
              </div>

              <p className={`${sectionLabel} mb-2`}>Select copies to undo</p>
              <div className="space-y-1.5">
                {undoEntries.map(({ sel, copiedRows, direction: dir }) => {
                  const key = `${dir}-${sel.id}`;
                  const checked = undoKeys.includes(key);
                  const totalRows = dir === 'outgoing' ? sel.rows.filter(r => r.productCode).length : currentCount;

                  return (
                    <button
                      key={key}
                      onClick={() => toggleUndoTarget(key)}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg border transition-all text-left ${
                        checked ? 'bg-red-50 border-red-300' : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div
                        className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                          checked ? 'bg-red-500 border-red-500' : 'border-slate-300'
                        }`}
                      >
                        {checked && <Check size={10} className="text-white" strokeWidth={3} />}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          {dir === 'outgoing' ? (
                            <ArrowRight size={11} className="text-slate-400 flex-shrink-0" />
                          ) : (
                            <ArrowLeft size={11} className="text-slate-400 flex-shrink-0" />
                          )}
                          <p className={`text-xs font-semibold ${checked ? 'text-red-700' : 'text-slate-800'}`}>
                            {dir === 'outgoing'
                              ? `${sel.name} (received from ${currentSel?.name})`
                              : `${sel.name} (copied into ${currentSel?.name})`}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          <span className="text-[11px] text-slate-500">{totalRows} total</span>
                          <span className="text-[10px] font-semibold text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full">
                            {copiedRows.length} will be removed
                          </span>
                          <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full">
                            {totalRows - copiedRows.length} kept
                          </span>
                        </div>
                      </div>

                      {checked && <AlertTriangle size={14} className="text-red-400 flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="px-6 py-3.5 border-t border-slate-200 bg-white flex items-center justify-between gap-3">
            <p className="text-xs text-slate-500">
              {undoKeys.length > 0 ? (
                <>
                  Removing copied rows from{' '}
                  <span className="font-semibold text-red-600">{undoKeys.length}</span>{' '}
                  relationship{undoKeys.length !== 1 ? 's' : ''}
                </>
              ) : (
                'Select relationships to undo'
              )}
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUndo}
                disabled={undoKeys.length === 0}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-red-500 hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg transition-all shadow-sm"
              >
                <Undo2 size={13} /> Undo {undoKeys.length || ''}
              </button>
            </div>
          </div>
        </>
      )}
    </Dialog>
  );
}